/**
 * Client-side mirror of
 * `apps.proposal.contract_validation.validate_contract` (`ezbuild_back`) —
 * pure, synchronous, no network call — so the Contract Validation card can
 * recalculate live while a user edits the form, before anything is saved.
 *
 * FIVE INDEPENDENT checks, each its own `{valid, warning, error, incomplete}`
 * state — never merged into one pass. A milestone missing its own duration
 * must never hide a real Sales Price mismatch, or vice versa; a document
 * with real Start/Completion dates but no milestone durations at all must
 * still report a real `valid`/`warning` Project Duration result.
 *
 * Any two of `{Duration, Start, End}` resolve the third wherever it's
 * missing, both at the project level (`resolveProjectSchedule`) and at each
 * milestone's own level (`milestoneResolvedDurationDays`) — the validator
 * derives what it can before validating, rather than requiring an explicit
 * value the data already determines. Start+End alone (no Duration typed in)
 * report a real `valid` Project Duration; a milestone with only two of its
 * own three values is checked-and-valid, not skipped.
 *
 * - **sales_price** — Sales Price vs. the SUM of every milestone's own
 *   price. `warning` on a mismatch.
 * - **project_duration_vs_dates** — the document's own RESOLVED Project
 *   Duration (explicit `duration`/`duration_type`, or derived from
 *   `start_date`/`completion_date` when no explicit value was entered) vs.
 *   its own `(start_date, completion_date)` span. Deliberately independent
 *   of milestone data entirely. `warning` on a real mismatch (only possible
 *   when an explicit Duration disagrees with the span).
 * - **milestone_duration_vs_own_dates** — each milestone's own duration vs.
 *   its own Target Start/Target End. A milestone with any two of its own
 *   three values is checked; with all three present, a real mismatch is
 *   `error` — a milestone whose own numbers disagree with its own dates is
 *   a real internal contradiction.
 * - **total_milestone_duration_vs_project_duration** — total milestone
 *   duration (each milestone's own RESOLVED duration, summed) vs. the
 *   document's own RESOLVED Project Duration. `warning` on a mismatch.
 *   Distinct from `project_duration_vs_dates`, which compares against the
 *   document's own date SPAN instead of the milestones.
 * - **milestone_dates_vs_project_dates** — each milestone's own Target
 *   Start/Target End vs. the document's own RESOLVED `(start, end)` span
 *   (only milestones with their own dates set are checked). `error` when a
 *   milestone falls outside the document's own stated span.
 *
 * Milestones are read across EVERY Project Type/Scope Group unconditionally
 * (the form's whole `phases` array — there is no "selected tab" concept),
 * and their normalized durations are simply summed (Phase 1: sequential,
 * non-overlapping — no scheduling/dependency model for a Contract's own
 * milestones).
 *
 * Day counting is INCLUSIVE everywhere (Sep 1 → Sep 10 = 10 days), and unit
 * normalization reuses `DocumentTemplates/shared/duration-date-calculator.ts`
 * (`durationToDays`, `parseCalendarDay`, `inclusiveDaysBetween`,
 * `DAYS_PER_UNIT` — including "years") rather than a duplicate table.
 *
 * `incomplete` beats `warning`/`error`/`valid` whenever a check's OWN inputs
 * can't be computed at all, even after resolution — never silently reads as
 * 0.
 *
 * Keep this in lockstep with `contract_validation.py` if either changes.
 */

import dayjs, { type Dayjs } from "dayjs"

import {
  durationToDays,
  inclusiveDaysBetween,
  parseCalendarDay
} from "../shared/duration-date-calculator.ts"
import type {
  ContractValidationFormValues,
  ContractValidationPhaseInput,
  ContractValidationResult,
  ContractValidationStatus,
  MilestoneDateViolation,
  MilestoneDatesVsProjectDatesValidation,
  MilestoneDurationMismatchExample,
  MilestoneDurationVsOwnDatesValidation,
  MilestoneOutsideExample,
  ProjectDurationVsDatesValidation,
  SalesPriceValidation,
  TotalMilestoneDurationVsProjectDurationValidation
} from "./types.ts"

const VALID: ContractValidationStatus = "valid"
const WARNING: ContractValidationStatus = "warning"
const ERROR: ContractValidationStatus = "error"
const INCOMPLETE: ContractValidationStatus = "incomplete"

// Same cap as the backend's `_MAX_NAMED_EXAMPLES` — enough for a real
// message without risking an unbounded list for a many-milestone document.
// The counts themselves are never capped, only the example list.
const MAX_NAMED_EXAMPLES = 10

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null
  const n = typeof value === "number" ? value : parseFloat(String(value))
  return Number.isFinite(n) ? n : null
}

// Rounds to cents before an equality check, so float arithmetic on money
// (e.g. 0.1 + 0.2) can never read as a spurious warning.
function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

/** Reads a form field's date-ish value the same as `parseCalendarDay`, but
 * tolerant of a plain `Date` object too (a shape antd never actually hands a
 * `DatePicker`-bound field, but one older test fixtures/callers might). */
function parseFormDate(value: unknown): Dayjs | null {
  if (!value) return null
  if (dayjs.isDayjs(value)) return parseCalendarDay(value)
  if (value instanceof Date) return parseCalendarDay(dayjs(value))
  if (typeof value === "string") return parseCalendarDay(value)
  return null
}

/** `null` when either date is missing OR the span is non-positive (an end
 * on/before its start is not a real duration) — mirrors the backend's own
 * `_inclusive_days_between`, never a false 0 or a negative number. */
function inclusiveSpanDays(start: Dayjs | null, end: Dayjs | null): number | null {
  if (!start || !end) return null
  const days = inclusiveDaysBetween(start, end)
  return days > 0 ? days : null
}

/** One milestone's own duration, normalized to days via the shared
 * days/weeks/months/years table — `null` when it cannot be (no
 * `duration_days` at all, an unrecognized unit, or a non-positive value). */
function normalizeDurationDays(
  durationDays: unknown,
  durationType: unknown
): number | null {
  const days = toNumber(durationDays)
  if (days === null) return null
  return durationToDays(days, durationType as string | null | undefined)
}

/** The document's own Project Duration field (`Contract.duration`/
 * `Proposal.duration`, a free-text `CharField` holding whatever the Duration
 * `InputNumber` sent), normalized to days. `null` when blank/unparseable —
 * mirrors the backend's `_document_duration_days`, which requires a plain
 * integer string (`int(raw)`), not a decimal. */
function documentDurationDays(
  durationRaw: unknown,
  durationTypeRaw: unknown
): number | null {
  if (durationRaw === null || durationRaw === undefined) return null
  const raw = String(durationRaw).trim()
  if (raw === "" || !/^-?\d+$/.test(raw)) return null
  return durationToDays(parseInt(raw, 10), durationTypeRaw as string | null | undefined)
}

/** SUM of every milestone's own price, across every Project Type/Scope
 * Group. `null` only when there are no milestones at all. A milestone's own
 * price defaults to 0 when blank/unparseable — mirroring `ProposalPhase.
 * budget`'s DB-level `default=0` (a saved milestone never truly lacks a
 * price; a momentarily-blank input while typing reads the same way). */
function milestoneTotalPrice(
  phases: ContractValidationPhaseInput[] | null | undefined
): number | null {
  if (!phases || phases.length === 0) return null
  return phases.reduce((sum, phase) => sum + (toNumber(phase.budget) ?? 0), 0)
}

/** The project-level "any two of `{Duration, Start, End}` resolve the
 * third" derivation — mirrors the backend's own `_resolve_project_schedule`
 * exactly. An explicit Duration always wins over the date span when both are
 * present (Check A still flags a genuine mismatch between them); with no
 * explicit Duration, the date span itself becomes the resolved duration;
 * with an explicit Duration and only one of Start/End, the missing date is
 * derived from it. */
function resolveProjectSchedule(
  durationRaw: unknown,
  durationTypeRaw: unknown,
  startRaw: unknown,
  endRaw: unknown
): {
  start: Dayjs | null
  end: Dayjs | null
  duration_days: number | null
  duration_was_explicit: boolean
} {
  const explicitDurationDays = documentDurationDays(durationRaw, durationTypeRaw)
  let start = parseFormDate(startRaw)
  let end = parseFormDate(endRaw)
  let durationDays = explicitDurationDays
  const spanDays = inclusiveSpanDays(start, end)

  if (durationDays === null && spanDays !== null) {
    durationDays = spanDays
  } else if (durationDays !== null && end === null && start !== null) {
    end = start.add(durationDays - 1, "day")
  } else if (durationDays !== null && start === null && end !== null) {
    start = end.subtract(durationDays - 1, "day")
  }

  return { start, end, duration_days: durationDays, duration_was_explicit: explicitDurationDays !== null }
}

/** One milestone's own EFFECTIVE duration — its explicit `duration_days` if
 * set, else derived from that milestone's own Target Start/Target End.
 * Mirrors the backend's own `_milestone_resolved_duration_days`. */
function milestoneResolvedDurationDays(phase: ContractValidationPhaseInput): number | null {
  const explicit = normalizeDurationDays(phase.duration_days, phase.duration_type)
  if (explicit !== null) return explicit
  return inclusiveSpanDays(parseFormDate(phase.start_date), parseFormDate(phase.end_date))
}

/** SUM of every milestone's own RESOLVED duration (explicit, or derived from
 * that milestone's own dates). `null` when there are no milestones, OR when
 * even one milestone is missing a usable duration — a missing/unresolvable
 * duration must never read as 0 days, since that would silently produce a
 * misleading "duration matches" result for data that was never actually
 * entered. */
function milestoneTotalDurationDays(
  phases: ContractValidationPhaseInput[] | null | undefined
): number | null {
  if (!phases || phases.length === 0) return null
  let total = 0
  for (const phase of phases) {
    const days = milestoneResolvedDurationDays(phase)
    if (days === null) return null
    total += days
  }
  return total
}

/** Check A — Sales Price vs. total milestone price. */
function validateSalesPrice(
  salesPriceRaw: unknown,
  phases: ContractValidationPhaseInput[] | null | undefined
): SalesPriceValidation {
  const salesPrice = toNumber(salesPriceRaw)
  const milestoneTotal = milestoneTotalPrice(phases)

  if (salesPrice === null || milestoneTotal === null) {
    return {
      status: INCOMPLETE,
      sales_price: salesPrice,
      milestone_total: milestoneTotal,
      difference: null
    }
  }

  const difference = round2(salesPrice - milestoneTotal)
  return {
    status: difference === 0 ? VALID : WARNING,
    sales_price: round2(salesPrice),
    milestone_total: round2(milestoneTotal),
    difference
  }
}

/** Check B — the document's own Project Duration field vs. its own
 * `(start_date, completion_date)` span. Deliberately independent of
 * milestone data entirely — a document with no milestones at all, or
 * incomplete ones, can still report a real valid/warning result here.
 * Start+End alone (no explicit Duration typed in) report a real `valid`
 * result — the date span itself, since there's no stated value to conflict
 * with; an explicit Duration is only compared against the span for a
 * genuine mismatch when BOTH exist. */
function validateProjectDurationVsDates(
  durationRaw: unknown,
  durationTypeRaw: unknown,
  startRaw: unknown,
  endRaw: unknown
): ProjectDurationVsDatesValidation {
  const durationDays = documentDurationDays(durationRaw, durationTypeRaw)
  const dateSpanDays = inclusiveSpanDays(parseFormDate(startRaw), parseFormDate(endRaw))

  if (durationDays !== null && dateSpanDays !== null) {
    const differenceDays = durationDays - dateSpanDays
    return {
      status: differenceDays === 0 ? VALID : WARNING,
      duration_days: durationDays,
      date_span_days: dateSpanDays,
      difference_days: differenceDays
    }
  }

  if (durationDays === null && dateSpanDays !== null) {
    return {
      status: VALID,
      duration_days: dateSpanDays,
      date_span_days: dateSpanDays,
      difference_days: 0
    }
  }

  return {
    status: INCOMPLETE,
    duration_days: durationDays,
    date_span_days: dateSpanDays,
    difference_days: null
  }
}

/** Check C — each milestone's own duration vs. its own Target Start/Target
 * End. A milestone with any TWO of its own three values (duration/start/end)
 * is checked — the third is exactly what those two determine, so it's
 * checked-and-valid, not skipped. All three present is what makes a real
 * mismatch (`error`) possible — a milestone whose own numbers disagree with
 * its own dates is a real internal contradiction. A milestone with only ONE
 * of the three isn't counted either way — that's a gap Check D/E's own
 * `incomplete` states already surface, not a mismatch this check should
 * invent. */
function validateMilestoneDurationVsOwnDates(
  phases: ContractValidationPhaseInput[] | null | undefined
): MilestoneDurationVsOwnDatesValidation {
  let checked = 0
  const mismatched: MilestoneDurationMismatchExample[] = []

  for (const phase of phases ?? []) {
    const durationDays = normalizeDurationDays(phase.duration_days, phase.duration_type)
    const mStart = parseFormDate(phase.start_date)
    const mEnd = parseFormDate(phase.end_date)
    const piecesPresent = [durationDays, mStart, mEnd].filter((v) => v !== null).length
    if (piecesPresent < 2) continue

    checked += 1
    if (durationDays !== null && mStart !== null && mEnd !== null) {
      const spanDays = inclusiveSpanDays(mStart, mEnd)
      if (spanDays !== durationDays) {
        mismatched.push({
          name: phase.name ?? "",
          duration_days: durationDays,
          date_span_days: spanDays
        })
      }
    }
  }

  if (checked === 0) {
    return { status: INCOMPLETE, checked_count: 0, mismatched_count: 0, mismatched_examples: [] }
  }
  return {
    status: mismatched.length > 0 ? ERROR : VALID,
    checked_count: checked,
    mismatched_count: mismatched.length,
    mismatched_examples: mismatched.slice(0, MAX_NAMED_EXAMPLES)
  }
}

/** Check D — total milestone duration vs. the project's own RESOLVED
 * Project Duration (an explicit value if stated, else derived from
 * Start+End — see `resolveProjectSchedule`). Distinct from Check B (which
 * compares Project Duration against the document's own date SPAN) — this
 * compares it against what the milestones themselves add up to. Milestones
 * are validated against a Start+End-derived Project Duration even when no
 * one ever typed a number into the Duration field. */
function validateTotalMilestoneDurationVsProjectDuration(
  durationRaw: unknown,
  durationTypeRaw: unknown,
  startRaw: unknown,
  endRaw: unknown,
  phases: ContractValidationPhaseInput[] | null | undefined
): TotalMilestoneDurationVsProjectDurationValidation {
  const projectDurationDays = resolveProjectSchedule(
    durationRaw,
    durationTypeRaw,
    startRaw,
    endRaw
  ).duration_days
  const milestoneTotalDays = milestoneTotalDurationDays(phases)

  if (projectDurationDays === null || milestoneTotalDays === null) {
    return {
      status: INCOMPLETE,
      project_duration_days: projectDurationDays,
      milestone_total_days: milestoneTotalDays,
      difference_days: null
    }
  }

  const differenceDays = projectDurationDays - milestoneTotalDays
  return {
    status: differenceDays === 0 ? VALID : WARNING,
    project_duration_days: projectDurationDays,
    milestone_total_days: milestoneTotalDays,
    difference_days: differenceDays
  }
}

/** Check E — each milestone's own Target Start/Target End vs. the
 * project's own RESOLVED `(start, end)` span (derived from whichever two of
 * Duration/Start/End are present, same as Check B/D — see
 * `resolveProjectSchedule`). Only milestones with their own dates set are
 * checked. A milestone starting before the project's own start, or ending
 * after its own end, is a real contradiction (`error`) — the document is
 * promising work outside the timeline it states for itself. */
function validateMilestoneDatesVsProjectDates(
  durationRaw: unknown,
  durationTypeRaw: unknown,
  startRaw: unknown,
  endRaw: unknown,
  phases: ContractValidationPhaseInput[] | null | undefined
): MilestoneDatesVsProjectDatesValidation {
  const resolved = resolveProjectSchedule(durationRaw, durationTypeRaw, startRaw, endRaw)
  const start = resolved.start
  const end = resolved.end
  if (!start || !end) {
    return { status: INCOMPLETE, checked_count: 0, outside_count: 0, outside_examples: [] }
  }

  let checked = 0
  const outside: MilestoneOutsideExample[] = []

  for (const phase of phases ?? []) {
    const mStart = parseFormDate(phase.start_date)
    const mEnd = parseFormDate(phase.end_date)
    if (!mStart && !mEnd) continue

    checked += 1
    const violations: MilestoneDateViolation[] = []
    if (mStart && mStart.isBefore(start)) violations.push("starts_before_project")
    if (mEnd && mEnd.isAfter(end)) violations.push("ends_after_project")

    if (violations.length > 0) {
      outside.push({
        name: phase.name ?? "",
        start_date: mStart ? mStart.format("YYYY-MM-DD") : null,
        end_date: mEnd ? mEnd.format("YYYY-MM-DD") : null,
        violations
      })
    }
  }

  if (checked === 0) {
    return { status: INCOMPLETE, checked_count: 0, outside_count: 0, outside_examples: [] }
  }
  return {
    status: outside.length > 0 ? ERROR : VALID,
    checked_count: checked,
    outside_count: outside.length,
    outside_examples: outside.slice(0, MAX_NAMED_EXAMPLES)
  }
}

/** The full five-check self-consistency validation for the Contract
 * currently in the form — reads ONLY the form's own current values, never
 * the Lead or a linked Proposal. */
function calculateContractValidation(
  values: ContractValidationFormValues
): ContractValidationResult {
  return {
    sales_price: validateSalesPrice(values.sales_price, values.phases),
    project_duration_vs_dates: validateProjectDurationVsDates(
      values.duration,
      values.duration_type,
      values.start_date,
      values.completion_date
    ),
    milestone_duration_vs_own_dates: validateMilestoneDurationVsOwnDates(values.phases),
    total_milestone_duration_vs_project_duration:
      validateTotalMilestoneDurationVsProjectDuration(
        values.duration,
        values.duration_type,
        values.start_date,
        values.completion_date,
        values.phases
      ),
    milestone_dates_vs_project_dates: validateMilestoneDatesVsProjectDates(
      values.duration,
      values.duration_type,
      values.start_date,
      values.completion_date,
      values.phases
    )
  }
}

type MilestoneTimelineStatus = "valid" | "warning" | "incomplete"

interface MilestoneTimelineVsProjectDurationValidation {
  status: MilestoneTimelineStatus
  project_duration_days: number | null
  milestone_timeline_days: number | null
  difference_days: number | null
}

/** The PROJECT's own timeline for `calculateMilestoneTimelineVsProjectDuration`
 * below: Start Date -> End Date whenever BOTH are stated (the actual
 * calendar span always wins here, even if it disagrees with an explicit
 * Duration field — this check is about the project's TIMELINE, not about
 * whether the stated Duration number matches its dates, which is the
 * separate, existing `project_duration_vs_dates` check above). Only when
 * one of Start/End is missing does an explicit Duration derive it, via
 * `resolveProjectSchedule`. */
function projectTimelineDays(
  durationRaw: unknown,
  durationTypeRaw: unknown,
  startRaw: unknown,
  endRaw: unknown
): number | null {
  const spanDays = inclusiveSpanDays(parseFormDate(startRaw), parseFormDate(endRaw))
  if (spanDays !== null) return spanDays
  return resolveProjectSchedule(durationRaw, durationTypeRaw, startRaw, endRaw).duration_days
}

/** The Validation card's own "Milestone duration / timeline does not match
 * Project duration" row — a SEPARATE, standalone comparison from the five
 * checks above, deliberately not folded into `ContractValidationResult`
 * (whose shape mirrors the backend's `validate_contract`/`validate_proposal`
 * response 1:1 — see this module's own docstring).
 *
 * Compares the PROJECT's own timeline (`projectTimelineDays`, above)
 * against the OVERALL MILESTONE TIMELINE: the earliest milestone Target
 * Start through the latest milestone Target End. This is deliberately NOT
 * the sum of each milestone's own duration (that sum-based comparison is
 * `total_milestone_duration_vs_project_duration`, above) — two milestones
 * can each be short but span a wide range if there's a gap between them,
 * and this check is about whether the overall milestone SCHEDULE fits the
 * project's own stated timeline.
 *
 * `warning` on a genuine mismatch; hidden (`valid`) when they agree;
 * `incomplete` only when there's truly no usable date on either side — a
 * document missing an explicit Duration is never `incomplete` here as long
 * as Start+End (or Start/End+Duration) let the project timeline resolve.
 */
function calculateMilestoneTimelineVsProjectDuration(values: {
  duration?: unknown
  duration_type?: unknown
  start_date?: unknown
  completion_date?: unknown
  phases?: ContractValidationPhaseInput[] | null
}): MilestoneTimelineVsProjectDurationValidation {
  const projectDurationDays = projectTimelineDays(
    values.duration,
    values.duration_type,
    values.start_date,
    values.completion_date
  )

  const starts: Dayjs[] = []
  const ends: Dayjs[] = []
  for (const phase of values.phases ?? []) {
    const start = parseFormDate(phase.start_date)
    const end = parseFormDate(phase.end_date)
    if (start) starts.push(start)
    if (end) ends.push(end)
  }

  const milestoneTimelineDays =
    starts.length > 0 && ends.length > 0
      ? inclusiveSpanDays(
          starts.reduce((earliest, d) => (d.isBefore(earliest) ? d : earliest)),
          ends.reduce((latest, d) => (d.isAfter(latest) ? d : latest))
        )
      : null

  if (projectDurationDays === null || milestoneTimelineDays === null) {
    return {
      status: "incomplete",
      project_duration_days: projectDurationDays,
      milestone_timeline_days: milestoneTimelineDays,
      difference_days: null
    }
  }

  const differenceDays = projectDurationDays - milestoneTimelineDays
  return {
    status: differenceDays === 0 ? "valid" : "warning",
    project_duration_days: projectDurationDays,
    milestone_timeline_days: milestoneTimelineDays,
    difference_days: differenceDays
  }
}

/** Whether any of the five checks is in `warning` or `error` state — the
 * single predicate both the live card and the Generate-time gate use to
 * decide "show something to review", so they can never disagree about what
 * counts as a mismatch. `incomplete` never counts — a data gap is not
 * something to review, only a real disagreement is. */
function contractValidationHasIssues(result: ContractValidationResult): boolean {
  return (
    result.sales_price.status === WARNING ||
    result.project_duration_vs_dates.status === WARNING ||
    result.milestone_duration_vs_own_dates.status === ERROR ||
    result.total_milestone_duration_vs_project_duration.status === WARNING ||
    result.milestone_dates_vs_project_dates.status === ERROR
  )
}

export {
  calculateContractValidation,
  calculateMilestoneTimelineVsProjectDuration,
  contractValidationHasIssues,
  milestoneTotalPrice,
  milestoneTotalDurationDays,
  normalizeDurationDays,
  documentDurationDays
}
export type { MilestoneTimelineStatus, MilestoneTimelineVsProjectDurationValidation }
